import * as React from 'react';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';
import update from 'immutability-helper';

import GET_GROUP_MEMBER_QUERY from './queries/GroupMemberProfile.gql';
import Notice from 'components/Notice';
import UsersProfileContainer, {
  Props as UsersProfileContainerProps,
} from './UsersProfileContainer';
import GroupMemberActionsMenuButton from 'user/components/GroupMemberActionsMenuButton';
import EventTracking from 'lib/EventTracking';

import {
  GroupMemberProfile_classes,
  GroupMemberProfile_classes_membersPaginated_members,
} from './queries/__generated__/GroupMemberProfile';

export function shapeMemberIntoUserProfile(
  member?: GroupMemberProfile_classes_membersPaginated_members
) {
  if (!member) {
    return null;
  }

  return update(
    {
      ...member,
      ...(member.user || {}),
      roles: member.org_affiliation_view?.roles || [member.user.role],
    },
    { $unset: ['user', 'role'] }
  );
}

type Props = UsersProfileContainerProps & { group: GroupMemberProfile_classes };

type State = { showPrivateNote: boolean };

class UsersProfileInGroupContainer extends React.Component<Props, State> {
  state = { showPrivateNote: false };

  setPrivateNoteVisibility = (visible: boolean) => {
    this.setState({
      showPrivateNote: visible,
    });
  };

  trackEvent = (key: string, properties: any = {}) => {
    EventTracking.trackKey(`class_people_list.${key}`, {
      group_id: this.props.group.id,
      ...properties,
    });
  };

  renderErrorBanner = () => {
    const { loading, loaded } = this.props;

    if (!loading && !loaded) {
      return (
        <Notice color="paper">
          This person is not a member of this class so you don’t have permission to view them.
        </Notice>
      );
    }

    return null;
  };

  renderUserActionsMenuButton = () => {
    const { group, member, onClose, onUserChanged } = this.props;
    return (
      <GroupMemberActionsMenuButton
        group={group}
        member={member}
        onUserRemoved={() => {
          this.trackEvent('user_profile.org_membership.removed');
          onClose();
          onUserChanged();
        }}
        onUserUpdate={async (eventName: string = 'user_profile.role.updated') => {
          this.trackEvent(eventName);
          await this.props.refetch();
        }}
        isPrivateNoteVisible={this.state.showPrivateNote}
        onRevealPrivateNote={() => this.setPrivateNoteVisibility(true)}
      />
    );
  };

  render() {
    return (
      <UsersProfileContainer
        {...this.props}
        entityName={this.props.group?.className}
        errorBanner={this.renderErrorBanner()}
        userActionsMenuButton={this.renderUserActionsMenuButton()}
        onHidePrivateNote={() => this.setPrivateNoteVisibility(false)}
        onShowPrivateNote={() => this.setPrivateNoteVisibility(true)}
        showPrivateNote={this.state.showPrivateNote}
        trackEvent={this.trackEvent}
      />
    );
  }
}

const withGroupMember = graphql(GET_GROUP_MEMBER_QUERY, {
  options: (props: Props) => {
    const { member, entityUuid } = props;

    return {
      variables: {
        groupUuid: entityUuid,
        userId: member.id,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps }: any) => {
    const loadedMember = shapeMemberIntoUserProfile(
      idx(data, _ => _.classes[0].membersPaginated.members[0])
    );

    const { rosteredStatus, sisFirstName, sisLastName } =
      data?.classes?.[0].organization?.members?.members?.[0] ?? {};

    const member = data.loading
      ? ownProps.member
      : {
          ...loadedMember,
          rosteredStatus,
          sisFirstName,
          sisLastName,
        } || ownProps.member;

    return {
      member,
      group: idx(data, _ => _.classes[0]),
      loading: data.loading,
      loaded: !!loadedMember,
      error: data.error,
      refetch: data.refetch,
    };
  },
  skip: ({ entityUuid }) => !entityUuid,
});

export default compose(withGroupMember)(UsersProfileInGroupContainer);
